import axios from "axios";
import ExpiryMap from "expiry-map";
import pMemoize from "p-memoize";
import LocalStorageManager from "../util/LocalStorageManager";

const baseURL = (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') ? (
    // Development will always refer to the .env file (REACT_APP_API_URI)
    process.env.REACT_APP_API_URI
) : (
    // Production always uses API in the same origin
    window.location.origin.toString()
)

// Main Axios instance used to make API calls
const HttpService = axios.create({
    baseURL,
    maxRedirects: 0,
    withCredentials: true,
    headers: {
        "X-Requested-With": "XMLHttpRequest"
    },
});

const memoizeOptions = {
    // maxAge: 10000
    cache: new ExpiryMap(10000)
}

// Flag to track if we're currently getting an app token
let isGettingAppToken = false;
// Queue for callbacks waiting for the app token
const appTokenCallbacks = [];

const getAppToken = async () => {
    // If we're already in the process of getting a token, wait for that to complete
    if (isGettingAppToken) {
        return new Promise((resolve, reject) => {
            appTokenCallbacks.push({ resolve, reject });
        });
    }
    
    // Check if we already have a token in storage
    const existingToken = LocalStorageManager.getAuthToken();
    if (existingToken) {
        // We have a token, but we don't check if it's expired here
        // The response interceptor will handle that
        return existingToken;
    }
    
    isGettingAppToken = true;
    
    try {
        console.log("Getting new app token");
        const response = await axios.post(
            `${baseURL}/api/login/app`,
            {
                secret: process.env.REACT_APP_API_SECRET
            }
        );
        
        const { token } = response.data;
    
        LocalStorageManager.setAuthToken(token);
        // Ensures previous user data is cleared
        LocalStorageManager.clearLoggedInUser();

        isGettingAppToken = false;
        
        // Resolve all pending callbacks
        appTokenCallbacks.forEach(callback => callback.resolve(token));
        appTokenCallbacks.length = 0;

        return token;
    } catch (error) {
        console.log("Failed to get app token:", error);
        LocalStorageManager.clearLoggedInUserAndToken();
        
        isGettingAppToken = false;
        
        // Reject all pending callbacks
        appTokenCallbacks.forEach(callback => callback.reject(error));
        appTokenCallbacks.length = 0;
        
        throw error;
    }
};

// Flag to track if we're currently refreshing a token
let isRefreshingToken = false;
// Queue for callbacks waiting for the token refresh
const refreshTokenCallbacks = [];

export const refreshToken = async () => {
    // If we're already in the process of refreshing a token, wait for that to complete
    if (isRefreshingToken) {
        return new Promise((resolve, reject) => {
            refreshTokenCallbacks.push({ resolve, reject });
        });
    }
    
    isRefreshingToken = true;
    
    try {
        console.log("Refreshing auth token");

        const response = await axios.post(
            `${baseURL}/api/login/refresh`
        );
    
        const { token } = response.data;

        LocalStorageManager.setAuthToken(token);
        
        isRefreshingToken = false;
        
        // Resolve all pending callbacks
        refreshTokenCallbacks.forEach(callback => callback.resolve(token));
        refreshTokenCallbacks.length = 0;

        return token;
    } catch (error) {
        console.log("Failed to refresh token:", error);
        LocalStorageManager.clearLoggedInUserAndToken();
        
        isRefreshingToken = false;
        
        // Reject all pending callbacks
        refreshTokenCallbacks.forEach(callback => callback.reject(error));
        refreshTokenCallbacks.length = 0;
        
        // When refresh token fails, we should not try to get a new app token
        // Instead, throw the error to let the caller handle authentication failure
        throw error;
    }
};

// Request interceptor
HttpService.interceptors.request.use(
    async (config) => {
        try {
            let accessToken = LocalStorageManager.getAuthToken();
            
            // If we don't have a token, get one
            if (!accessToken) {
                try {
                    accessToken = await getAppToken();
                } catch (error) {
                    console.error("Failed to get app token in request interceptor:", error);
                    // Continue with the request even if token retrieval fails
                    // The response interceptor will handle 401 errors
                }
            }
            
            if (accessToken) {
                config.headers = {
                    ...config.headers,
                    Authorization: `Bearer ${accessToken}`,
                };
            }
    
            return config;
        } catch(error) {
            return Promise.reject(error);
        }
    },
    (error) => Promise.reject(error)
);

// Response interceptor
HttpService.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (!axios.isCancel(error)) {
            const config = error?.config;
            // Check if this is an unauthorized error (401)
            const isUnauthorized = error?.response?.status === 401 || 
                                 (process.env.NODE_ENV === 'development' && !error.response);
            
            // Prevent infinite retry loop by checking if we've already tried to retry
            if (config && !config._retry && isUnauthorized) {
                config._retry = true; // Mark that we're retrying this request
                
                try {
                    let accessToken;
                    
                    // If we already have a token, try to refresh it
                    if (LocalStorageManager.getAuthToken()) {
                        try {
                            accessToken = await refreshToken();
                        } catch (refreshError) {
                            console.error("Failed to refresh token:", refreshError);
                            // Only clear auth state if the refresh error indicates the token is truly invalid
                            // For 401/403 errors, clear auth state. For network errors, don't.
                            if (refreshError?.response?.status === 401 || refreshError?.response?.status === 403) {
                                LocalStorageManager.clearLoggedInUserAndToken();
                            }
                            return Promise.reject(error);
                        }
                    } else {
                        // If we don't have a token at all, get a new app token
                        accessToken = await getAppToken();
                    }
                    
                    if (accessToken) {
                        // Update the Authorization header with the new token
                        config.headers = {
                            ...config.headers,
                            Authorization: `Bearer ${accessToken}`,
                        };
                        
                        // Retry the original request with the new token
                        return axios(config);
                    }
                } catch (retryError) {
                    console.error("Failed to get any valid token for retry:", retryError);
                    return Promise.reject(error); // Return the original error if token retrieval fails
                }
            }
        }

        return Promise.reject(error);
    }
);

export { getAppToken }; // Only export getAppToken here, as refreshToken is already exported above
export default HttpService;